/**
 * 報告書の「作業内容記入欄」の必須チェック。
 *
 * フード報告書・床報告書は report_work_items（作業名称/作業内容/数量/単位）、
 * フリー報告書は other_works（作業名称/作業内容/数量/単位）を対象とする。
 *
 * 【アプリ側との基準差（意図的）】
 * スマホアプリの提出時バリデーションは「0件」もエラーにするが、WEB管理画面では
 * 0件を許容する。改修前に提出された作業項目0件の報告書を管理者が開き、住所など
 * 無関係な項目だけを直したいケースで保存が塞がるのを避けるため。
 *
 * 【既存項目の扱い（意図的）】
 * name/content/quantity/unit はDB上すべてnullable。この必須化より前に保存された
 * 報告書には、1件以上あるが一部の項目だけ空欄という状態が正規に存在し得る。
 * WEBは保存時に配列を丸ごと送り直すため、読み込み時から存在した項目まで空欄
 * チェックの対象にすると、無関係な項目を直すだけの保存が過去データのせいで
 * 止まってしまう。そのため、必須チェックは「この画面で新規追加した項目」だけに
 * 適用し、読み込み時から存在した項目（originalIds に含まれるもの）は素通しする。
 * 新規追加した項目のidは Date.now() の仮ID（DBの連番と衝突しない大きな値）。
 */

type WorkItemLike = {
  id?: number;
  name?: string | null;
  content?: string | null;
  quantity?: number | null;
  unit?: string | null;
};

type OtherWorkLike = {
  id?: number;
  work_type?: string | null;
  details?: string | null;
  quantity?: number | null;
  unit?: string | null;
};

type OriginalIds = ReadonlySet<number> | ReadonlyArray<number> | null | undefined;

const isBlank = (v?: string | null) => !v || v.trim() === "";

/** 数量は未入力・0 をどちらも未入力として扱う（アプリ側と同一基準） */
const isBlankQuantity = (v?: number | null) => v === null || v === undefined || v === 0;

const toIdSet = (ids: OriginalIds): ReadonlySet<number> =>
  ids instanceof Set ? ids : new Set(ids ?? []);

/**
 * フード・床報告書の作業項目を検証し、未入力項目のラベル一覧を返す。
 * 戻り値が空配列なら保存可。作業項目そのものが0件の場合も保存可とする。
 *
 * @param originalIds 読み込み時から存在した項目のid一覧。渡した場合、それらの
 *   項目は空欄チェックの対象外になる（上記「既存項目の扱い」を参照）。
 */
export function validateReportWorkItems(
  items?: Array<WorkItemLike> | null,
  originalIds?: OriginalIds
): string[] {
  const errors: string[] = [];
  const list = items ?? [];
  const existing = toIdSet(originalIds);

  // 0件は許容する（上記「アプリ側との基準差」を参照）
  list.forEach((item, index) => {
    if (item.id !== undefined && existing.has(item.id)) return;

    const prefix = list.length > 1 ? `作業項目 ${index + 1}: ` : "";
    if (isBlank(item.name)) errors.push(`${prefix}作業名称`);
    if (isBlank(item.content)) errors.push(`${prefix}作業内容`);
    if (isBlankQuantity(item.quantity)) errors.push(`${prefix}数量`);
    if (isBlank(item.unit)) errors.push(`${prefix}単位`);
  });

  return errors;
}

/**
 * フリー報告書のその他作業を検証し、未入力項目のラベル一覧を返す。
 * 戻り値が空配列なら保存可。その他作業そのものが0件の場合も保存可とする。
 *
 * @param originalIds 読み込み時から存在した項目のid一覧。渡した場合、それらの
 *   項目は空欄チェックの対象外になる（上記「既存項目の扱い」を参照）。
 */
export function validateOtherWorks(
  works?: Array<OtherWorkLike> | null,
  originalIds?: OriginalIds
): string[] {
  const errors: string[] = [];
  const list = works ?? [];
  const existing = toIdSet(originalIds);

  // 0件は許容する（上記「アプリ側との基準差」を参照）
  list.forEach((work, index) => {
    if (work.id !== undefined && existing.has(work.id)) return;

    const prefix = list.length > 1 ? `その他作業 ${index + 1}: ` : "";
    if (isBlank(work.work_type)) errors.push(`${prefix}作業名称`);
    if (isBlank(work.details)) errors.push(`${prefix}作業内容`);
    if (isBlankQuantity(work.quantity)) errors.push(`${prefix}数量`);
    if (isBlank(work.unit)) errors.push(`${prefix}単位`);
  });

  return errors;
}

/** 未入力項目一覧を alert 用の文面へ整形する。 */
export function formatWorkValidationMessage(errors: string[]): string {
  return ["以下の必須項目を入力してください：", ...errors.map((e) => `・${e}`)].join(
    "\n"
  );
}
